import { LiveData, Service } from '@toeverything/infra';
import dayjs from 'dayjs';

import { AuthService, type WorkspaceServerService } from '../../cloud';
import type { DocsService } from '../../doc';
import type { FolderMutationOutboxService } from '../../folder-api';
import type {
  AccessibleFolderCacheStore,
  AccessibleFolderMeta,
} from '../../organize';
import type { TemplateDocService } from '../../template-doc';
import type { WorkspaceService } from '../../workspace';
import type { JournalStore } from '../store/journal';

export type MaybeDate = Date | string | number;

export const JOURNAL_DATE_FORMAT = 'YYYY-MM-DD';
export const JOURNAL_FOLDER_NAME = 'Journal';

function privateRootFolderId(workspaceId: string, userId: string): string {
  return `private_${workspaceId}_${userId}`;
}

function privateAreaRootsFolderId(workspaceId: string): string {
  return `private_area_roots_${workspaceId}`;
}

export function journalFolderId(privateRootId: string): string {
  return `${privateRootId}_journal`;
}

function createOptimisticFolderMeta({
  id,
  immutable = false,
  inheritPermissions,
  name,
  ownerUserId,
  parentId,
  systemKind = null,
  visibility,
}: {
  id: string;
  immutable?: boolean;
  inheritPermissions: boolean;
  name: string;
  ownerUserId: string | null;
  parentId: string | null;
  systemKind?: string | null;
  visibility: string;
}): AccessibleFolderMeta {
  return {
    id,
    name,
    parentId,
    ownerUserId,
    systemKind,
    visibility,
    inheritPermissions,
    isWorkspaceRoot: false,
    immutable,
    storageLimitBytes: null,
    storageUsedBytes: null,
    metadataOnly: false,
    docIds: [],
  };
}

export class JournalService extends Service {
  constructor(
    private readonly store: JournalStore,
    private readonly docsService: DocsService,
    private readonly templateDocService: TemplateDocService,
    private readonly workspaceService: WorkspaceService,
    private readonly workspaceServerService: WorkspaceServerService,
    private readonly accessibleFolderCache: AccessibleFolderCacheStore,
    private readonly folderMutationOutbox: FolderMutationOutboxService
  ) {
    super();
  }

  allJournalDates$ = this.store.allJournalDates$;
  allJournalDocIds$ = this.store.allJournalDocIds$;

  journalDate$(docId: string) {
    return LiveData.from(this.store.watchDocJournalDate(docId), undefined);
  }
  journalToday$(docId: string) {
    return LiveData.computed(get => {
      const date = get(this.journalDate$(docId));
      if (!date) return false;
      return dayjs(date).isSame(dayjs(), 'day');
    });
  }

  setJournalDate(docId: string, date: string) {
    this.store.setDocJournalDate(docId, date);
  }

  removeJournalDate(docId: string) {
    this.store.removeDocJournalDate(docId);
  }

  journalsByDate$(date: string) {
    return this.store.docsByJournalDate$(date);
  }

  private getCurrentAccountId(): string | null {
    return (
      this.workspaceServerService.server?.scope.getOptional(AuthService)
        ?.session.account$.value?.id ?? null
    );
  }

  private ensureOptimisticPrivateRoot(
    workspaceId: string,
    accountId: string,
    privateRootId: string
  ): void {
    const cachedRoot =
      this.accessibleFolderCache.getCachedFolder(privateRootId);
    if (cachedRoot?.systemKind === 'private_root') {
      return;
    }
    if (this.accessibleFolderCache.hasCompletedServerFetch()) {
      return;
    }

    this.accessibleFolderCache.optimisticUpsertFolder(
      createOptimisticFolderMeta({
        id: privateRootId,
        immutable: true,
        inheritPermissions: false,
        name: 'Private Folder',
        ownerUserId: accountId,
        parentId: privateAreaRootsFolderId(workspaceId),
        systemKind: 'private_root',
        visibility: 'private',
      })
    );
  }

  private getCachedJournalFolder(
    privateRootId: string
  ): AccessibleFolderMeta | null {
    return (
      this.accessibleFolderCache
        .getCachedFolders()
        .find(
          folder =>
            folder.parentId === privateRootId &&
            folder.name === JOURNAL_FOLDER_NAME &&
            folder.systemKind !== 'private_mail'
        ) ?? null
    );
  }

  private ensureJournalFolder(): string | undefined {
    const workspace = this.workspaceService.workspace;
    if (!workspace || workspace.flavour === 'local') {
      return undefined;
    }

    const accountId = this.getCurrentAccountId();
    if (!accountId) {
      return undefined;
    }

    const privateRootId = privateRootFolderId(workspace.id, accountId);
    const cachedPrivateRoot =
      this.accessibleFolderCache.getCachedFolder(privateRootId);
    if (cachedPrivateRoot && cachedPrivateRoot.systemKind !== 'private_root') {
      return undefined;
    }
    if (
      !cachedPrivateRoot &&
      this.accessibleFolderCache.hasCompletedServerFetch()
    ) {
      return undefined;
    }

    this.ensureOptimisticPrivateRoot(workspace.id, accountId, privateRootId);

    const cachedJournalFolder = this.getCachedJournalFolder(privateRootId);
    if (cachedJournalFolder) {
      return cachedJournalFolder.id;
    }

    const folderId = journalFolderId(privateRootId);
    if (!this.accessibleFolderCache.getCachedFolder(folderId)) {
      this.accessibleFolderCache.optimisticUpsertFolder(
        createOptimisticFolderMeta({
          id: folderId,
          inheritPermissions: true,
          name: JOURNAL_FOLDER_NAME,
          ownerUserId: accountId,
          parentId: privateRootId,
          visibility: 'private',
        }),
        { preserveUntilServerAck: true }
      );

      void this.folderMutationOutbox
        .enqueueCreateFolder({
          folderId,
          name: JOURNAL_FOLDER_NAME,
          parentId: privateRootId,
          visibility: 'private',
          inheritPermissions: true,
        })
        .catch(error => {
          console.error('Failed to queue Journal folder creation:', error);
        });
    }

    return folderId;
  }

  private createJournal(maybeDate: MaybeDate) {
    const day = dayjs(maybeDate);
    const title = day.format(JOURNAL_DATE_FORMAT);
    const targetFolderId = this.ensureJournalFolder();
    const docRecord = this.docsService.createDoc(
      targetFolderId
        ? {
            title,
            targetFolderId,
          }
        : {
            title,
            createInWorkspaceRoot: true,
          }
    );
    // set created date to match the journal date
    docRecord.setMeta({
      createDate: dayjs()
        .set('year', day.year())
        .set('month', day.month())
        .set('date', day.date())
        .toDate()
        .getTime(),
    });

    const enablePageTemplate =
      this.templateDocService.setting.enablePageTemplate$.value;
    const pageTemplateDocId =
      this.templateDocService.setting.pageTemplateDocId$.value;
    const journalTemplateDocId =
      this.templateDocService.setting.journalTemplateDocId$.value;
    // if journal template configured
    if (journalTemplateDocId) {
      this.docsService
        .duplicateFromTemplate(journalTemplateDocId, docRecord.id)
        .catch(console.error);
    }
    // journal template not configured, use page template
    else if (enablePageTemplate && pageTemplateDocId) {
      this.docsService
        .duplicateFromTemplate(pageTemplateDocId, docRecord.id)
        .catch(console.error);
    }
    this.setJournalDate(docRecord.id, title);
    return docRecord;
  }

  ensureJournalByDate(maybeDate: MaybeDate) {
    const day = dayjs(maybeDate);
    const title = day.format(JOURNAL_DATE_FORMAT);
    const docs = this.journalsByDate$(title).value;
    if (docs.length) return docs[0];
    return this.createJournal(maybeDate);
  }
}
